import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { economicGroupApi, type IEconomicGroup } from '@/api/commercial/economic-groups';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { EconomicGroupFormDialog } from './economic-group-form-dialog';

const formatCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

interface EconomicGroupManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMutationSuccess?: () => void;
}

export function EconomicGroupManagementDialog({
  open,
  onOpenChange,
  onMutationSuccess,
}: EconomicGroupManagementDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingGroup, setEditingGroup] = useState<IEconomicGroup | undefined>();
  const [deleteGroup, setDeleteGroup] = useState<IEconomicGroup | null>(null);
  const [deleteLinkedCount, setDeleteLinkedCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['economic-groups'],
    queryFn: () => economicGroupApi.findMany({ limit: 100 }),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: (payload: Omit<IEconomicGroup, 'id'>) => economicGroupApi.create(payload),
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: 'Grupo econômico criado com sucesso', variant: 'success' });
        setFormOpen(false);
        queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
        onMutationSuccess?.();
      } else {
        toast({
          title: result.error?.global || 'Erro ao criar grupo econômico',
          variant: 'destructive',
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Omit<IEconomicGroup, 'id'>> }) =>
      economicGroupApi.update(id, payload),
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: 'Grupo econômico atualizado com sucesso', variant: 'success' });
        setFormOpen(false);
        queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
        onMutationSuccess?.();
      } else {
        toast({
          title: result.error?.global || 'Erro ao atualizar grupo econômico',
          variant: 'destructive',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => economicGroupApi.delete(id),
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: 'Grupo econômico excluído com sucesso', variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
        onMutationSuccess?.();
      } else {
        toast({
          title: result.error?.global || 'Erro ao excluir grupo econômico',
          variant: 'destructive',
        });
      }
      setDeleteGroup(null);
    },
  });

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditingGroup(undefined);
    setFormOpen(true);
  };

  const handleOpenEdit = (group: IEconomicGroup) => {
    setFormMode('edit');
    setEditingGroup(group);
    setFormOpen(true);
  };

  const handleFormSubmit = (data: Omit<IEconomicGroup, 'id'>) => {
    if (formMode === 'create') {
      createMutation.mutate(data);
    } else if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, payload: data });
    }
  };

  const handleOpenDelete = async (group: IEconomicGroup) => {
    setDeleteGroup(group);
    setDeleteLinkedCount(null);
    setIsLoadingCount(true);
    try {
      const count = await economicGroupApi.countLinkedAccounts(group.id);
      setDeleteLinkedCount(count);
    } catch {
      setDeleteLinkedCount(null);
    } finally {
      setIsLoadingCount(false);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteGroup) {
      deleteMutation.mutate(deleteGroup.id);
    }
  };

  const groups = (data?.docs || []).filter((g) => g.economic_group_holding);

  const holdingLabel = (group: IEconomicGroup) => {
    if (group.economic_group_holding) {
      return (
        <div>
          <p className="font-medium text-sm">{group.economic_group_holding.name}</p>
          <p className="text-xs text-muted-foreground font-mono">
            {formatCnpj(group.economic_group_holding.taxId)}
          </p>
        </div>
      );
    }
    return <span className="text-sm text-muted-foreground">Sem holding</span>;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerenciar Grupos Econômicos</DialogTitle>
          </DialogHeader>

          <div className="flex justify-end">
            <Button onClick={handleOpenCreate} className="shrink-0">
              <Plus className="h-4 w-4 mr-1" />
              Novo Grupo
            </Button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum grupo econômico cadastrado
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Holding</TableHead>
                  <TableHead>Controladas</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>{holdingLabel(group)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {group.economic_group_controlled?.length || 0} empresas
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(group)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDelete(group)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                          Deletar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Form sub-dialog */}
      <EconomicGroupFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        mode={formMode}
        initialData={editingGroup}
        onSubmit={handleFormSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteGroup} onOpenChange={(isOpen) => !isOpen && setDeleteGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir grupo econômico</AlertDialogTitle>
            <AlertDialogDescription>
              {isLoadingCount ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verificando vínculos...
                </span>
              ) : deleteLinkedCount && deleteLinkedCount > 0 ? (
                <>
                  Já existem <strong>{deleteLinkedCount}</strong> empresas cadastradas no sistema
                  vinculadas ao grupo econômico
                  {deleteGroup?.economic_group_holding
                    ? ` "${deleteGroup.economic_group_holding.name}"`
                    : ''}
                  . Apagar esse grupo econômico vai deixar as empresas sem grupo econômico.
                </>
              ) : (
                <>
                  Tem certeza que deseja excluir o grupo econômico
                  {deleteGroup?.economic_group_holding
                    ? ` "${deleteGroup.economic_group_holding.name}"`
                    : ''}
                  ? Esta ação não pode ser desfeita.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending || isLoadingCount}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
